import React from "react";
import { DotLegalDatePicker, DotLegalTextField } from "@dotlegal/dotlegal-ui-components";
import MentionInput from "../common/components/mentionInput/MentionInput.tsx";
import { useTranslation } from "../localization/useTranslation.ts";
import { EntityType } from "../common/Common.types.ts";
import { TagRelationType } from "../common/components/tagsMultiSelect/TagsMultiSelect.types.ts";
import TagsMultiSelectStandAlone from "../common/components/tagsMultiSelect/tagsMultiSelectAutoUpdater/TagsMultiSelectAutoUpdater.tsx";
import { ContractModel } from "./contract/Contract.types.ts";
import {
    ContractStandardField,
    EntityTemplateField,
    getStandardFieldTranslationKey,
    isContractStandardField,
    TemplateEntityType,
} from "../entityTemplates/EntityTemplate.types.ts";
import { Box, Collapse } from "@mui/material";
import ContractDocuments from "./contract/contractDocument/contractDocuments/ContractDocuments.tsx";
import DocumentsEmptyState from "@common/components/documentsEmptyState/DocumentsEmptyState.tsx";
import CounterPartiesStep from "./contract/steps/counterPartiesStep/CounterPartiesStep.tsx";
import CounterParty from "./contract/steps/counterPartiesStep/counterParty/CounterParty.tsx";
import { InputFieldTypeEnum } from "../entityTemplates/inputFields/InputFields.types.ts";
import { exhaustiveGuard } from "../common/utilities.ts";
import { isNullOrWhitespace } from "../common/stringOperations.ts";
import ContractTypeField from "./contract/fields/ContractTypeField.tsx";
import BusinessAreaField from "../entityTemplates/fields/BusinessAreaField.tsx";
import CurrencyField from "./contract/fields/CurrencyField.tsx";
import PaymentTermsField from "./contract/fields/PaymentTermsField.tsx";
import ContractManagerField from "./contract/fields/ContractManagerField.tsx";
import AdditionalAccessField from "./contract/fields/AdditionalAccessField.tsx";
import ExcludedUsersField from "./contract/fields/ExcludedUsersField.tsx";
import ContractAccessBusinessAreaField from "./contract/fields/ContractAccessBusinessAreaField.tsx";
import ContractGroupCompaniesAccessField from "./contract/fields/ContractGroupCompaniesAccessField.tsx";
import VisibleToAllUsersField from "./contract/fields/VisibleToAllUsersField.tsx";
import { ValidationError } from "../common/validationError.ts";
import CustomTextField from "../entityTemplates/fields/customFields/CustomTextField.tsx";
import CustomRichTextField from "../entityTemplates/fields/customFields/CustomRichTextField.tsx";
import CustomSingleSelectField from "../entityTemplates/fields/customFields/CustomSingleSelectField.tsx";
import CustomCountryField from "../entityTemplates/fields/customFields/CustomCountryField.tsx";
import CustomMultiSelectField from "../entityTemplates/fields/customFields/CustomMultiSelectField.tsx";
import CustomDateField from "../entityTemplates/fields/customFields/CustomDateField.tsx";
import CustomNumberField from "../entityTemplates/fields/customFields/CustomNumberField.tsx";
import CustomAmountField from "../entityTemplates/fields/customFields/CustomAmountField.tsx";
import CustomCheckbox from "../entityTemplates/fields/customFields/CustomCheckbox.tsx";
import CustomLegalEntityField from "../entityTemplates/fields/customFields/CustomLegalEntityField.tsx";
import CustomUserField from "../entityTemplates/fields/customFields/CustomUserField.tsx";
import CustomCalculatedField from "../entityTemplates/fields/customFields/CustomCalculatedField.tsx";
import CustomDatePeriodField from "../entityTemplates/fields/customFields/CustomDatePeriodField.tsx";
import { ComputedFieldTemplateEditing } from "../entityTemplates/fields/customFields/computedField.types.ts";
import AutoRenewalDurationField from "./contract/fields/AutoRenewalDurationField.tsx";
import { isAutoRenewalShown } from "./contract/fields/AutoRenewalDurationField.utils.ts";
import ContractStatusField from "./contract/fields/ContractStatusField.tsx";
import { useUserContext } from "../auth/userContextProvider/UserContextProvider.tsx";
import { UserPermissions } from "../auth/userContextProvider/UserContextProvider.types.ts";
import { useRemoveContractDate } from "./useRemoveContractDate.ts";

// Types
export interface StandardFieldProps {
    field: EntityTemplateField;
    isLoading: boolean;
    contract?: ContractModel;
    contractId: string;
    onChange: (changeFunc: (contract: ContractModel) => void) => Promise<void>;
    onRefetchContract: () => void;
    validationErrors: Array<ValidationError>;
    disabled: boolean;
    readOnly?: boolean; // For template editor preview
    isPreview: boolean;
    permissions: UserPermissions;
    disableAccessWarning?: boolean;
    showWarningAsInfo?: boolean;
    /** Suppresses every help icon in the field, for a view that shows the same field twice. */
    hideToolTip?: boolean;
    /** Turns off each field's own margin, for a caller that owns the vertical rhythm itself. */
    noMargin?: boolean;
    /** Leaves the renewal period out of the end date, for a caller that renders it as a row of its own. */
    hideAutoRenewal?: boolean;
    /** Puts the cursor in the name field, for a form whose first thing to fill in is the name. */
    autoFocusName?: boolean;
    /** Reports rich text on every edit instead of on blur, for a form whose Save button depends on the value. */
    reportEveryChange?: boolean;
    /** Puts each label above its input with the help icon beside it, for a form laid out that way. */
    useFixedLabel?: boolean;
}

export interface CustomFieldProps {
    entityId: string;
    contract?: ContractModel;
    field: EntityTemplateField;
    onChange: (changeFunc: (contract: ContractModel) => void) => void;
    disabled: boolean;
    readOnly?: boolean; // For template editor preview
    isPreview: boolean; // For template editor preview
    isLoading: boolean;
    validationErrors: Array<ValidationError>;
    computedFieldEditing?: ComputedFieldTemplateEditing; // For template editor preview
    /** Suppresses every help icon in the field, for a view that shows the same field twice. */
    hideToolTip?: boolean;
    /** Turns off each field's own margin, for a caller that owns the vertical rhythm itself. */
    noMargin?: boolean;
    /** Puts each label above its input with the help icon beside it, for a form laid out that way. */
    useFixedLabel?: boolean;
}

export interface FieldRendererProps {
    field: EntityTemplateField;
    // Standard field props
    contract?: ContractModel;
    isLoading: boolean;
    contractId?: string;
    onChange: (changeFunc: (contract: ContractModel) => void) => Promise<void>;
    onRefetchContract: () => void;
    validationErrors?: Array<{ field: string; error: string }>;
    permissions: UserPermissions;

    // Custom field props
    onCustomFieldChange: (changeFunc: (contract: ContractModel) => void) => void;
    // Common props
    readOnly?: boolean;
    isPreview: boolean;

    // Template editor preview props
    computedFieldEditing?: ComputedFieldTemplateEditing; // For template editor preview
}

// Standard Field Renderer Component
export const StandardFieldRenderer: React.FC<StandardFieldProps> = ({
    field,
    contract,
    contractId,
    onChange,
    onRefetchContract,
    validationErrors,
    disabled: disabledByCaller,
    readOnly = false,
    isPreview,
    isLoading = false,
    permissions,
    disableAccessWarning,
    showWarningAsInfo,
    hideToolTip = false,
    noMargin = false,
    hideAutoRenewal = false,
    autoFocusName = false,
    reportEveryChange = false,
    useFixedLabel = false,
}) => {
    const disabled = disabledByCaller || field.disabled;
    const { language } = useUserContext();
    const { translateString, getDateTimePickerLanguage } = useTranslation();
    const { onRemoveContractDate, isCheckingDate } = useRemoveContractDate(onChange);

    const seperatorStyle = language === "en" ? "en" : "da";

    if (!field.standardFieldIdentifier || !isContractStandardField(field.standardFieldIdentifier)) {
        return null;
    }

    const standardField = field.standardFieldIdentifier;

    switch (standardField) {
        case ContractStandardField.Status:
            return (
                <ContractStatusField
                    noMargin={noMargin}
                    hideToolTip={hideToolTip}
                    status={contract?.status}
                    onChange={
                        readOnly
                            ? () => {}
                            : (status) =>
                                  onChange((c) => {
                                      c.status = status;
                                  })
                    }
                    readOnly={readOnly}
                    disabled={disabled}
                    errorText={validationErrors.find((v) => v.field === getStandardFieldTranslationKey(ContractStandardField.Status))?.error}
                    isLoading={isLoading}
                    contractId={contractId}
                    contractEndDate={contract?.endDate}
                    contractStartDate={contract?.startDate}
                    onRefetchContract={onRefetchContract}
                />
            );

        case ContractStandardField.Name:
            return (
                <DotLegalTextField
                    autoFocus={autoFocusName}
                    noMargin={noMargin}
                    label={translateString("contract")}
                    value={contract?.name ?? ""}
                    debounce={!readOnly}
                    toolTipText={hideToolTip ? undefined : translateString("addContractNameTooltip")}
                    onChange={
                        readOnly
                            ? () => {}
                            : (value) =>
                                  onChange((c) => {
                                      c.name = value;
                                  })
                    }
                    errorText={validationErrors.find((v) => v.field === getStandardFieldTranslationKey(ContractStandardField.Name))?.error}
                    disabled={disabled}
                />
            );

        case ContractStandardField.Description:
            return (
                <MentionInput
                    noMargin={noMargin}
                    multiline
                    rows={5}
                    label={translateString("description")}
                    value={contract?.description ?? ""}
                    debounce
                    reportEveryChange={reportEveryChange}
                    useFixedLabel={useFixedLabel}
                    toolTipText={hideToolTip ? undefined : translateString("contractDescriptionTooltip")}
                    onChange={
                        readOnly
                            ? () => {}
                            : (value) =>
                                  onChange((c) => {
                                      c.description = value;
                                  })
                    }
                    disabled={disabled}
                    errorText={validationErrors.find((v) => v.field === getStandardFieldTranslationKey(ContractStandardField.Description))?.error}
                    {...(isNullOrWhitespace(contractId)
                        ? { disableMentions: true as const }
                        : { mentionSource: { entityFromId: contractId, entityFromType: EntityType.Contract } })}
                />
            );

        case ContractStandardField.ReferenceNumber:
            return (
                <DotLegalTextField
                    noMargin={noMargin}
                    label={translateString("referenceNumber")}
                    value={contract?.referenceNumber ?? ""}
                    debounce={!readOnly}
                    toolTipText={hideToolTip ? undefined : translateString("contractReferenceNumberTooltip")}
                    onChange={
                        readOnly
                            ? () => {}
                            : (value) =>
                                  onChange((c) => {
                                      c.referenceNumber = value;
                                  })
                    }
                    disabled={disabled}
                    errorText={validationErrors.find((v) => v.field === getStandardFieldTranslationKey(ContractStandardField.ReferenceNumber))?.error}
                />
            );

        case ContractStandardField.ContractType:
            return (
                <ContractTypeField
                    noMargin={noMargin}
                    useFixedLabel={useFixedLabel}
                    hideToolTip={hideToolTip}
                    isLoading={isLoading}
                    onChange={onChange}
                    readOnly={readOnly}
                    required={field.required}
                    contractTypeId={contract?.contractTypeId}
                    disabled={disabled}
                    isPreview={isPreview}
                    errorText={validationErrors.find((v) => v.field === getStandardFieldTranslationKey(ContractStandardField.ContractType))?.error}
                />
            );

        case ContractStandardField.ContractManager:
            return (
                <ContractManagerField
                    noMargin={noMargin}
                    hideToolTip={hideToolTip}
                    isLoading={isLoading}
                    contract={contract}
                    onChange={onChange}
                    readOnly={readOnly}
                    required={field.required}
                    isDisabled={disabled}
                    isPreview={isPreview}
                    disableAccessWarning={disableAccessWarning}
                    showWarningAsInfo={showWarningAsInfo}
                    errorText={validationErrors.find((v) => v.field === getStandardFieldTranslationKey(ContractStandardField.ContractManager))?.error}
                />
            );

        case ContractStandardField.AdditionalUserAccess:
            return (
                <AdditionalAccessField
                    noMargin={noMargin}
                    hideToolTip={hideToolTip}
                    isLoading={isLoading}
                    contract={contract}
                    onChange={onChange}
                    readOnly={readOnly}
                    required={field.required}
                    isDisabled={disabled}
                    isPreview={isPreview}
                    disableAccessWarning={disableAccessWarning}
                    showWarningAsInfo={showWarningAsInfo}
                    errorText={validationErrors.find((v) => v.field === getStandardFieldTranslationKey(ContractStandardField.AdditionalUserAccess))?.error}
                />
            );

        case ContractStandardField.ExcludedUsers:
            return (
                <ExcludedUsersField
                    noMargin={noMargin}
                    hideToolTip={hideToolTip}
                    contractId={contractId}
                    isLoading={isLoading}
                    contract={contract}
                    onChange={onChange}
                    readOnly={readOnly}
                    required={field.required}
                    isDisabled={disabled}
                    isPreview={isPreview}
                    disableAccessWarning={disableAccessWarning}
                    showWarningAsInfo={showWarningAsInfo}
                    errorText={validationErrors.find((v) => v.field === getStandardFieldTranslationKey(ContractStandardField.ExcludedUsers))?.error}
                />
            );

        case ContractStandardField.BusinessAreas:
            return (
                <BusinessAreaField
                    noMargin={noMargin}
                    useFixedLabel={useFixedLabel}
                    businessAreas={contract?.businessAreas}
                    isLoading={isLoading}
                    onChange={
                        readOnly
                            ? () => {}
                            : (value) =>
                                  onChange((c) => {
                                      c.businessAreas = value;
                                  })
                    }
                    readOnly={readOnly}
                    disabled={disabled}
                    isPreview={isPreview}
                    toolTipText={hideToolTip ? undefined : translateString("contractBusinessAreaTooltip")}
                    errorText={validationErrors.find((v) => v.field === getStandardFieldTranslationKey(ContractStandardField.BusinessAreas))?.error}
                />
            );

        case ContractStandardField.BusinessAreaAccess:
            return (
                <ContractAccessBusinessAreaField
                    noMargin={noMargin}
                    hideToolTip={hideToolTip}
                    contract={contract}
                    isLoading={isLoading}
                    onChange={onChange}
                    readOnly={readOnly}
                    disabled={disabled}
                    isPreview={isPreview}
                    disableAccessWarning={disableAccessWarning}
                    showWarningAsInfo={showWarningAsInfo}
                    errorText={validationErrors.find((v) => v.field === getStandardFieldTranslationKey(ContractStandardField.BusinessAreaAccess))?.error}
                />
            );

        case ContractStandardField.VisibleToAllUsers:
            return (
                <VisibleToAllUsersField
                    contract={contract}
                    onChange={onChange}
                    readOnly={readOnly}
                    isDisabled={disabled}
                    disableAccessWarning={disableAccessWarning}
                    errorText={validationErrors.find((v) => v.field === getStandardFieldTranslationKey(ContractStandardField.VisibleToAllUsers))?.error}
                />
            );

        case ContractStandardField.LegalEntities: {
            return (
                <ContractGroupCompaniesAccessField
                    contract={contract}
                    isLoading={isLoading}
                    onChange={onChange}
                    addGroupEntityDialogHeader={translateString("addCompaniesToContract")}
                    readOnly={readOnly}
                    disabled={disabled}
                    isPreview={isPreview}
                    errorText={validationErrors.find((v) => v.field === getStandardFieldTranslationKey(ContractStandardField.LegalEntities))?.error}
                    disableAccessWarning={disableAccessWarning}
                />
            );
        }

        case ContractStandardField.Tags:
            if (permissions.canAccessTags || isPreview) {
                return (
                    <TagsMultiSelectStandAlone
                        entityId={contractId}
                        isLoading={false}
                        entityType={TagRelationType.Contract}
                        disabled={disabled}
                        tooltipText={hideToolTip ? undefined : translateString("contractTagsTooltip")}
                        errorText={validationErrors.find((v) => v.field === getStandardFieldTranslationKey(ContractStandardField.Tags))?.error}
                        disableQuery={isPreview}
                    />
                );
            }
            return <></>;

        case ContractStandardField.Note:
            return (
                <MentionInput
                    noMargin={noMargin}
                    multiline
                    rows={5}
                    label={translateString("note")}
                    value={contract?.note ?? ""}
                    debounce={!readOnly}
                    reportEveryChange={reportEveryChange}
                    useFixedLabel={useFixedLabel}
                    onChange={
                        readOnly
                            ? () => {}
                            : (value) =>
                                  onChange((c) => {
                                      c.note = value;
                                  })
                    }
                    disabled={disabled}
                    errorText={validationErrors.find((v) => v.field === getStandardFieldTranslationKey(ContractStandardField.Note))?.error}
                    {...(isNullOrWhitespace(contractId)
                        ? { disableMentions: true as const }
                        : { mentionSource: { entityFromId: contractId, entityFromType: EntityType.Contract } })}
                />
            );

        // Period Step fields
        case ContractStandardField.StartDate: {
            let errorText = validationErrors.find((v) => v.field === getStandardFieldTranslationKey(ContractStandardField.StartDate))?.error;
            const combinedError = errorText;
            return (
                <DotLegalDatePicker
                    noMargin={noMargin}
                    label={translateString("startDate")}
                    value={contract?.startDate ?? null}
                    onChange={
                        readOnly
                            ? () => {}
                            : (value) => {
                                  if (value) {
                                      onChange((c) => {
                                          c.startDate = value;
                                      });
                                  } else {
                                      onRemoveContractDate(contractId, ContractStandardField.StartDate);
                                  }
                              }
                    }
                    disabled={disabled || isCheckingDate}
                    language={getDateTimePickerLanguage()}
                    helperMessage={combinedError}
                    error={!isNullOrWhitespace(combinedError)}
                />
            );
        }

        case ContractStandardField.EndDate: {
            let errorText = validationErrors.find((v) => v.field === getStandardFieldTranslationKey(ContractStandardField.EndDate))?.error;
            const combinedError = errorText;
            const autoRenewalEnabled = isAutoRenewalShown(field, contract?.endDate);
            return (
                <>
                    <DotLegalDatePicker
                        noMargin={noMargin}
                        label={translateString("endDate")}
                        value={contract?.endDate ?? null}
                        onChange={
                            readOnly
                                ? () => {}
                                : (value) => {
                                      if (value) {
                                          onChange((c) => {
                                              c.endDate = value;
                                          });
                                      } else {
                                          onRemoveContractDate(contractId, ContractStandardField.EndDate);
                                      }
                                  }
                        }
                        disabled={disabled || isCheckingDate}
                        language={getDateTimePickerLanguage()}
                        helperMessage={combinedError}
                        error={!isNullOrWhitespace(combinedError)}
                    />

                    {/* Left out entirely rather than collapsed: a collapsed field is still mounted, and the caller
                        renders the period elsewhere, so a second copy bound to the same value would fight the
                        first over it (each pushes its own text back up) and the number flickers. */}
                    {!hideAutoRenewal && (
                        <Collapse in={autoRenewalEnabled}>
                            <AutoRenewalDurationField
                                hideToolTip={hideToolTip}
                                noMargin={noMargin}
                                autoRenewalDuration={contract?.autoRenewalDuration}
                                autoRenewalDurationType={contract?.autoRenewalDurationType}
                                onChange={onChange}
                                disabled={disabled}
                                readOnly={readOnly ?? false}
                            />
                        </Collapse>
                    )}
                </>
            );
        }

        case ContractStandardField.SignatureDate: {
            let errorText = validationErrors.find((v) => v.field === getStandardFieldTranslationKey(ContractStandardField.SignatureDate))?.error;
            return (
                <DotLegalDatePicker
                    noMargin={noMargin}
                    label={translateString("signatureDate")}
                    useFixedLabel={useFixedLabel}
                    value={contract?.signatureDate ?? null}
                    onChange={
                        readOnly
                            ? () => {}
                            : (value) =>
                                  onChange((c) => {
                                      c.signatureDate = value ? value : undefined;
                                  })
                    }
                    disabled={disabled}
                    language={getDateTimePickerLanguage()}
                    helperMessage={errorText}
                    error={!isNullOrWhitespace(errorText)}
                />
            );
        }

        // Terms Step fields
        case ContractStandardField.TotalAmount:
            return (
                <DotLegalTextField
                    noMargin={noMargin}
                    onlyNumber={"decimal"}
                    label={translateString("totalAmount")}
                    value={contract?.totalAmount?.toString() ?? ""}
                    debounce={!readOnly}
                    onChange={
                        readOnly
                            ? () => {}
                            : (val) =>
                                  onChange((c) => {
                                      c.totalAmount = val ? parseFloat(val) : undefined;
                                  })
                    }
                    disabled={disabled}
                    toolTipText={hideToolTip ? undefined : translateString("contractTotalAmountTooltip")}
                    endAdorment={
                        isNullOrWhitespace(contract?.currencyCode) ? undefined : (
                            <Box sx={(theme) => ({ color: theme.palette.grey[400], fontSize: 12 })}>{contract!.currencyCode}</Box>
                        )
                    }
                    seperatorStyle={seperatorStyle}
                    errorText={validationErrors.find((v) => v.field === getStandardFieldTranslationKey(ContractStandardField.TotalAmount))?.error}
                />
            );

        case ContractStandardField.FinalisedAmount:
            return (
                <DotLegalTextField
                    noMargin={noMargin}
                    onlyNumber={"decimal"}
                    label={translateString("finalisedAmount")}
                    value={contract?.finalizedAmount?.toString() ?? ""}
                    debounce={!readOnly}
                    onChange={
                        readOnly
                            ? () => {}
                            : (val) =>
                                  onChange((c) => {
                                      c.finalizedAmount = val ? parseFloat(val) : undefined;
                                  })
                    }
                    disabled={disabled}
                    toolTipText={hideToolTip ? undefined : translateString("contractFinalizedAmountTooltip")}
                    endAdorment={
                        isNullOrWhitespace(contract?.currencyCode) ? undefined : (
                            <Box sx={(theme) => ({ color: theme.palette.grey[400], fontSize: 12 })}>{contract!.currencyCode}</Box>
                        )
                    }
                    seperatorStyle={seperatorStyle}
                    errorText={validationErrors.find((v) => v.field === getStandardFieldTranslationKey(ContractStandardField.FinalisedAmount))?.error}
                />
            );

        case ContractStandardField.Currency:
            return (
                <CurrencyField
                    noMargin={noMargin}
                    useFixedLabel={useFixedLabel}
                    hideToolTip={hideToolTip}
                    currencyCode={contract?.currencyCode}
                    onChange={onChange}
                    readOnly={readOnly}
                    isDisabled={disabled}
                    required={field.required}
                    isLoading={isLoading}
                    errorText={validationErrors.find((v) => v.field === getStandardFieldTranslationKey(ContractStandardField.Currency))?.error}
                />
            );

        case ContractStandardField.PaymentTerm:
            return (
                <PaymentTermsField
                    noMargin={noMargin}
                    useFixedLabel={useFixedLabel}
                    hideToolTip={hideToolTip}
                    isLoading={isLoading}
                    paymentTermId={contract?.paymentTermId}
                    readOnly={readOnly}
                    onChange={onChange}
                    isDisabled={disabled}
                    required={field.required}
                    isPreview={isPreview}
                    errorText={validationErrors.find((v) => v.field === getStandardFieldTranslationKey(ContractStandardField.PaymentTerm))?.error}
                />
            );

        case ContractStandardField.SpecialTerms:
            return (
                <DotLegalTextField
                    noMargin={noMargin}
                    multiline
                    rows={5}
                    label={translateString("specialTerms")}
                    useFixedLabel={useFixedLabel}
                    value={contract?.specialTerms ?? ""}
                    debounce={!readOnly}
                    onChange={
                        readOnly
                            ? () => {}
                            : (value) =>
                                  onChange((c) => {
                                      c.specialTerms = value;
                                  })
                    }
                    disabled={disabled}
                    errorText={validationErrors.find((v) => v.field === getStandardFieldTranslationKey(ContractStandardField.SpecialTerms))?.error}
                />
            );

        case ContractStandardField.NotificationOfContractExpiry:
            return (
                <DotLegalTextField
                    noMargin={noMargin}
                    onlyNumber={"integer"}
                    label={translateString("notificationOfContractExpiry")}
                    value={contract?.contractExpiryNotificationInDays?.toString() ?? ""}
                    debounce={!readOnly}
                    onChange={
                        readOnly
                            ? () => {}
                            : (val) =>
                                  onChange((c) => {
                                      c.contractExpiryNotificationInDays = val ? parseInt(val) : undefined;
                                  })
                    }
                    disabled={disabled}
                    toolTipText={hideToolTip ? undefined : translateString("notificationOfContractExpiryTooltip")}
                    endAdorment={<Box sx={(theme) => ({ color: theme.palette.grey[400], fontSize: 12 })}>{translateString("days")}</Box>}
                    errorText={
                        validationErrors.find((v) => v.field === getStandardFieldTranslationKey(ContractStandardField.NotificationOfContractExpiry))?.error
                    }
                />
            );
        case ContractStandardField.Documents:
            if (isPreview) {
                return <DocumentsEmptyState />;
            } else {
                return (
                    <ContractDocuments
                        contractId={contractId}
                        onUpdate={onRefetchContract}
                        contractName={contract?.name ?? ""}
                        emptyStateWrappedInPaper
                        readOnly={disabled}
                        errorText={validationErrors.find((v) => v.field === getStandardFieldTranslationKey(ContractStandardField.Documents))?.error}
                    />
                );
            }

        case ContractStandardField.CounterParties:
            if (isPreview) {
                return (
                    <CounterParty
                        counterParty={{ id: "", legalEntityId: undefined, subContractors: [] }}
                        isExpanded
                        onExpandClick={() => {}}
                        onRemoveCounterParty={() => {}}
                        onClearParty={() => {}}
                        partyOptions={[]}
                        legalEntityData={[]}
                        validationErrors={[]}
                        legalEntityIsLoading={false}
                        onCounterPartyChange={async () => {}}
                        onSubContractorChange={async () => {}}
                        contractId={contractId}
                        selectedLegalentities={[]}
                        hasSubContractors={false}
                        showAddNewEntityButton
                        partyPlaceholder={translateString("chooseInternalContractParty")}
                        readonly
                    />
                );
            } else {
                return (
                    <Box
                        sx={{
                            "& > *:first-child": {
                                // Your styles here - examples:
                                width: "100%",
                            },
                        }}
                    >
                        <CounterPartiesStep contractId={contractId} contract={contract!} validationErrors={validationErrors} readOnly={disabled} />
                    </Box>
                );
            }

        default:
            return exhaustiveGuard(standardField);
    }
};

// Custom Field Renderer Component
export const CustomFieldRenderer: React.FC<CustomFieldProps> = ({
    contract,
    entityId,
    field,
    onChange,
    disabled: disabledByCaller = false,
    readOnly = false,
    isPreview,
    isLoading = false,
    validationErrors,
    computedFieldEditing,
    hideToolTip = false,
    noMargin = false,
    useFixedLabel = false,
}) => {
    const disabled = disabledByCaller || field.disabled;
    if (!field.customInputFieldType) {
        return null;
    }

    const value = contract?.customFieldValues.find((x) => x.customInputFieldId == field.customInputFieldId)?.value ?? "";
    switch (field.customInputFieldType!) {
        case InputFieldTypeEnum.TextField:
            return (
                <CustomTextField
                    noMargin={noMargin}
                    isLoading={isLoading}
                    customInputFieldId={field.customInputFieldId!}
                    entityId={entityId}
                    entityApiPath="contract"
                    fieldName={field.customInputFieldName!}
                    fieldDescription={hideToolTip ? undefined : field.customInputFieldDescription}
                    useFixedLabel={useFixedLabel}
                    value={value as string}
                    onChange={onChange}
                    readOnly={readOnly}
                    isDisabled={disabled}
                    isPreview={isPreview}
                    errorText={validationErrors.find((v) => v.field === field.id)?.error}
                />
            );

        case InputFieldTypeEnum.RichTextField:
            return (
                <CustomRichTextField
                    noMargin={noMargin}
                    entityId={entityId}
                    entityApiPath="contract"
                    customInputFieldId={field.customInputFieldId!}
                    fieldName={field.customInputFieldName!}
                    fieldDescription={hideToolTip ? undefined : field.customInputFieldDescription}
                    useFixedLabel={useFixedLabel}
                    value={value as string}
                    onChange={onChange}
                    readOnly={readOnly}
                    isDisabled={disabled}
                    isPreview={isPreview}
                    isLoading={isLoading}
                    errorText={validationErrors.find((v) => v.field === field.id)?.error}
                />
            );

        case InputFieldTypeEnum.CountryField:
            return (
                <CustomCountryField
                    noMargin={noMargin}
                    entityId={entityId}
                    entityApiPath="contract"
                    customInputFieldId={field.customInputFieldId!}
                    fieldName={field.customInputFieldName!}
                    fieldDescription={hideToolTip ? undefined : field.customInputFieldDescription}
                    useFixedLabel={useFixedLabel}
                    value={value as string}
                    onChange={onChange}
                    readOnly={readOnly}
                    isDisabled={disabled}
                    isPreview={isPreview}
                    isLoading={isLoading}
                    errorText={validationErrors.find((v) => v.field === field.id)?.error}
                />
            );

        case InputFieldTypeEnum.SingleDropDown:
            return (
                <CustomSingleSelectField
                    noMargin={noMargin}
                    entityId={entityId}
                    entityApiPath="contract"
                    customInputFieldId={field.customInputFieldId!}
                    fieldName={field.customInputFieldName!}
                    fieldDescription={hideToolTip ? undefined : field.customInputFieldDescription}
                    useFixedLabel={useFixedLabel}
                    value={value as string}
                    onChange={onChange}
                    readOnly={readOnly}
                    isDisabled={disabled}
                    isPreview={isPreview}
                    isLoading={isLoading}
                    errorText={validationErrors.find((v) => v.field === field.id)?.error}
                />
            );

        case InputFieldTypeEnum.MultiDropDown:
            return (
                <CustomMultiSelectField
                    noMargin={noMargin}
                    isLoading={isLoading}
                    entityId={entityId}
                    entityApiPath="contract"
                    customInputFieldId={field.customInputFieldId!}
                    fieldName={field.customInputFieldName!}
                    fieldDescription={hideToolTip ? undefined : field.customInputFieldDescription}
                    useFixedLabel={useFixedLabel}
                    values={value as string[]}
                    onChange={onChange}
                    readOnly={readOnly}
                    isDisabled={disabled}
                    isPreview={isPreview}
                    errorText={validationErrors.find((v) => v.field === field.id)?.error}
                />
            );

        case InputFieldTypeEnum.DateField:
            return (
                <CustomDateField
                    noMargin={noMargin}
                    isLoading={isLoading}
                    entityId={entityId}
                    entityApiPath="contract"
                    customInputFieldId={field.customInputFieldId!}
                    fieldName={field.customInputFieldName!}
                    fieldDescription={hideToolTip ? undefined : field.customInputFieldDescription}
                    useFixedLabel={useFixedLabel}
                    value={value as Date | null}
                    onChange={onChange}
                    readOnly={readOnly}
                    isDisabled={disabled}
                    isPreview={isPreview}
                    errorText={validationErrors.find((v) => v.field === field.id)?.error}
                />
            );

        case InputFieldTypeEnum.NumberField:
            return (
                <CustomNumberField
                    noMargin={noMargin}
                    isLoading={isLoading}
                    entityId={entityId}
                    entityApiPath="contract"
                    customInputFieldId={field.customInputFieldId!}
                    fieldName={field.customInputFieldName!}
                    fieldDescription={hideToolTip ? undefined : field.customInputFieldDescription}
                    useFixedLabel={useFixedLabel}
                    value={value as string}
                    onChange={onChange}
                    readOnly={readOnly}
                    isDisabled={disabled}
                    isPreview={isPreview}
                    errorText={validationErrors.find((v) => v.field === field.id)?.error}
                />
            );

        case InputFieldTypeEnum.AmountField:
            return (
                <CustomAmountField
                    isLoading={isLoading}
                    entityId={entityId}
                    entityApiPath="contract"
                    customInputFieldId={field.customInputFieldId!}
                    fieldName={field.customInputFieldName!}
                    fieldDescription={hideToolTip ? undefined : field.customInputFieldDescription}
                    useFixedLabel={useFixedLabel}
                    value={value as { Amount: number; CurrencyCode: string }}
                    onChange={onChange}
                    readOnly={readOnly}
                    isDisabled={disabled}
                    isPreview={isPreview}
                    errorText={validationErrors.find((v) => v.field === field.id)?.error}
                />
            );

        case InputFieldTypeEnum.CheckboxField:
            return (
                <CustomCheckbox
                    isLoading={isLoading}
                    entityId={entityId}
                    entityApiPath="contract"
                    customInputFieldId={field.customInputFieldId!}
                    fieldName={field.customInputFieldName!}
                    fieldDescription={hideToolTip ? undefined : field.customInputFieldDescription}
                    value={value as boolean}
                    onChange={onChange}
                    readOnly={readOnly}
                    isDisabled={disabled}
                    isPreview={isPreview}
                    errorText={validationErrors.find((v) => v.field === field.id)?.error}
                />
            );

        case InputFieldTypeEnum.LegalEntityField:
            return (
                <CustomLegalEntityField
                    noMargin={noMargin}
                    isLoading={isLoading}
                    entityId={entityId}
                    entityApiPath="contract"
                    customInputFieldId={field.customInputFieldId!}
                    fieldName={field.customInputFieldName!}
                    fieldDescription={hideToolTip ? undefined : field.customInputFieldDescription}
                    useFixedLabel={useFixedLabel}
                    values={value as string[]}
                    onChange={onChange}
                    readOnly={readOnly}
                    isDisabled={disabled}
                    isPreview={isPreview}
                    errorText={validationErrors.find((v) => v.field === field.id)?.error}
                />
            );

        case InputFieldTypeEnum.UserField:
            return (
                <CustomUserField
                    noMargin={noMargin}
                    isLoading={isLoading}
                    entityId={entityId}
                    entityApiPath="contract"
                    customInputFieldId={field.customInputFieldId!}
                    fieldName={field.customInputFieldName!}
                    fieldDescription={hideToolTip ? undefined : field.customInputFieldDescription}
                    useFixedLabel={useFixedLabel}
                    values={value as string[]}
                    onChange={onChange}
                    readOnly={readOnly}
                    isDisabled={disabled}
                    isPreview={isPreview}
                    errorText={validationErrors.find((v) => v.field === field.id)?.error}
                />
            );

        case InputFieldTypeEnum.CalculatedField:
            return (
                <CustomCalculatedField
                    field={field}
                    isLoading={isLoading}
                    fieldDescription={hideToolTip ? undefined : field.customInputFieldDescription}
                    isPreview={isPreview}
                    errorText={validationErrors.find((v) => v.field === field.id)?.error}
                    templateEntityType={TemplateEntityType.Contract}
                    computedFieldEditing={computedFieldEditing}
                    entityTemplateId={contract?.entityTemplateId}
                    entity={{ customFieldValues: contract?.customFieldValues ?? [], startDate: contract?.startDate, endDate: contract?.endDate }}
                />
            );

        case InputFieldTypeEnum.DatePeriodField:
            return (
                <CustomDatePeriodField
                    field={field}
                    isLoading={isLoading}
                    fieldDescription={hideToolTip ? undefined : field.customInputFieldDescription}
                    isPreview={isPreview}
                    errorText={validationErrors.find((v) => v.field === field.id)?.error}
                    templateEntityType={TemplateEntityType.Contract}
                    computedFieldEditing={computedFieldEditing}
                    entity={{ customFieldValues: contract?.customFieldValues ?? [], startDate: contract?.startDate, endDate: contract?.endDate }}
                />
            );

        default:
            exhaustiveGuard(field.customInputFieldType!);
    }
};

// Main Field Renderer - can be used by both template editor and contract form
export const ContractFieldRenderer: React.FC<FieldRendererProps> = (props) => {
    const disabled = props.isPreview || props.readOnly || !props.permissions.contractManagementPermissions.permissions?.edit;
    if (props.field.standardFieldIdentifier) {
        return (
            <StandardFieldRenderer
                field={props.field}
                contract={props.contract}
                contractId={props.contractId!}
                onChange={props.onChange}
                validationErrors={props.validationErrors || []}
                readOnly={props.readOnly}
                isPreview={props.isPreview}
                permissions={props.permissions}
                isLoading={props.isLoading}
                disabled={disabled}
                onRefetchContract={props.onRefetchContract}
            />
        );
    } else {
        return (
            <CustomFieldRenderer
                contract={props.contract}
                field={props.field}
                onChange={props.onCustomFieldChange}
                readOnly={props.readOnly}
                isPreview={props.isPreview}
                disabled={disabled}
                entityId={props.contractId!}
                isLoading={props.isLoading}
                validationErrors={props.validationErrors || []}
                computedFieldEditing={props.computedFieldEditing}
            />
        );
    }
};
